/**
 * ProfileInfoSection - секция «Профиль».
 * Сотрудник (логин и ФИО), роль из БД в цветах бейджа, текущая стартовая страница и ее выбор.
 * Кабинет аптеки (role.targetInterface = dashboard) дополнительно видит параметры аптеки и статус синхронизатора.
 */
import React from 'react';
import { Box, Button, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import { PersonOutline, Refresh } from '@mui/icons-material';
import SectionHeaderDivider from './SectionHeaderDivider';
import StartPagePicker from './StartPagePicker';
import { formatPhoneWithSpaces, isCurrentTimeWithinWorkingHours } from '../utils';
import { roleAccentColor } from '../roleBadge';
import { ProfileMe, ProfileStartPage, SyncStatus } from '../profileApi';
import { StartPageOption } from '../../../config/startPages';

export interface ProfileInfoSectionProps {
  me: ProfileMe;
  locationText: string;
  /** null - кабинет офиса или статус не удалось проверить */
  sync: SyncStatus | null;
  syncChecking: boolean;
  onRefreshSync: () => void;
  startPage: StartPageOption | null;
  /** У роли нет прав на стартовую страницу - при входе откроется профиль */
  startPageBlocked: boolean;
  onStartPageSaved: (next: ProfileStartPage) => void;
}

// Колонки - по ширине секции, а не окна: рядом с боковым меню профиля секция заметно уже окна.
// Сводка - три карточки в ряд или столбиком; плитки аптеки - 1/2/3 колонки, чтобы шесть плиток заполняли ряды целиком
const SUMMARY_WIDE = '@container (min-width: 620px)';
const TILES_MID = '@container (min-width: 420px)';
const TILES_WIDE = '@container (min-width: 640px)';
const ROW_WIDE = '@container (min-width: 520px)';

const CAPTION_SX = { color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' } as const;

const CARD_SX = {
  p: 2.5,
  borderRadius: '16px',
  bgcolor: '#f8fafc',
  border: '1px solid #e2e8f0',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  minWidth: 0
} as const;

const InfoTile: React.FC<{ title: string; value: React.ReactNode; hint: string }> = ({ title, value, hint }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: '12px',
      bgcolor: '#ffffff',
      border: '1px solid #e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: 84,
      minWidth: 0
    }}
  >
    <Typography variant="caption" sx={CAPTION_SX}>{title}</Typography>
    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b', mt: 0.5, fontSize: '1.05rem', wordBreak: 'break-word' }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.25 }}>{hint}</Typography>
  </Box>
);

const filled = (value: string | null | undefined, fallback: string): string => {
  const text = String(value || '').trim();
  return text && text !== '-' && text !== 'NaN' ? text : fallback;
};

const ProfileInfoSection: React.FC<ProfileInfoSectionProps> = ({
  me,
  locationText,
  sync,
  syncChecking,
  onRefreshSync,
  startPage,
  startPageBlocked,
  onStartPageSaved
}) => {
  const { profile, role } = me;
  const isDashboard = role.targetInterface === 'dashboard';
  const roleColor = roleAccentColor(role.badge);
  const pharmacyNum = filled(profile.pharmacyNum, '');
  const workingTime = isCurrentTimeWithinWorkingHours(profile.workingHours);
  // Пустой список - сервер еще не отдает доступные страницы (не перезапущен): выбор не показываем
  const canPickStartPage = me.startPage.available.length > 0;

  const syncState = (() => {
    if (!sync) return { text: 'Не проверен', hint: 'Не удалось получить статус службы', alarm: false, dot: '#94a3b8' };
    if (sync.isRunning) return { text: sync.statusText || 'Запущен', hint: 'Служба обмена данными', alarm: false, dot: '#10b981' };
    return {
      text: sync.statusText || 'Не запущен',
      hint: workingTime ? 'Служба обмена остановлена' : 'Нерабочее время - работа не блокируется',
      alarm: workingTime,
      dot: '#ef4444'
    };
  })();

  return (
    <Paper
      id="section-profile"
      elevation={0}
      sx={{
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: '20px',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        scrollMarginTop: '24px',
        containerType: 'inline-size'
      }}
    >
      <SectionHeaderDivider
        id="header-profile"
        icon={<PersonOutline sx={{ fontSize: 22 }} />}
        title="Профиль"
        subtitle="Сводная информация об учетной записи в корпоративной системе сети Zerde"
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2.5, [SUMMARY_WIDE]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' } }}>
        <Box sx={CARD_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography variant="caption" sx={CAPTION_SX}>Сотрудник</Typography>
            <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {profile.username}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.25, wordBreak: 'break-word' }}>
            {filled(profile.fullName, 'ФИО не указано')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>{locationText}</Typography>
        </Box>

        <Box sx={CARD_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" sx={CAPTION_SX}>Роль</Typography>
            {me.impersonated && (
              <Tooltip title="Сейчас в профиле работает администратор от вашего имени" arrow>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#b45309' }}>Вход администратора</Typography>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', minHeight: 24, borderRadius: '2px', bgcolor: roleColor, flexShrink: 0 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.25, wordBreak: 'break-word' }}>
              {role.name}
            </Typography>
          </Box>
        </Box>

        <Box sx={CARD_SX}>
          <Typography variant="caption" sx={CAPTION_SX}>Стартовая страница</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.25, wordBreak: 'break-word' }}>
            {startPage ? startPage.label : 'Профиль пользователя'}
          </Typography>
          {startPageBlocked && (
            <Typography variant="caption" sx={{ color: '#b45309' }}>
              При входе откроется профиль: у роли нет доступа к этой странице
            </Typography>
          )}
        </Box>
      </Box>

      {canPickStartPage && (
        <StartPagePicker targetInterface={role.targetInterface} startPage={me.startPage} onSaved={onStartPageSaved} />
      )}

      {isDashboard && (
        <Box
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: '16px',
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            containerType: 'inline-size'
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.05rem' }}>
              Параметры аптеки
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Реквизиты подразделения, логистическая привязка, адрес и контакты
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 2,
              [TILES_MID]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
              [TILES_WIDE]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }
            }}
          >
            <InfoTile title="Номер аптеки" value={pharmacyNum ? `№ ${pharmacyNum}` : 'Не привязана'} hint="Учетный номер точки" />
            <InfoTile title="Регион аптеки" value={filled(profile.regionName, 'Не указан')} hint="Региональный филиал" />
            <InfoTile title="Склад РЦ" value={filled(profile.belongsStorage, 'Не привязан')} hint="Распределительный центр" />
            <InfoTile title="Время работы" value={filled(profile.workingHours, 'Не указано')} hint="Из персональных данных" />
            <InfoTile title="Телефон филиала" value={formatPhoneWithSpaces(profile.workPhone) || 'Не указан'} hint="Рабочий номер подразделения" />
            <InfoTile title="Телефон заведующей" value={formatPhoneWithSpaces(profile.userphone) || 'Не указан'} hint="Из персональных данных" />

            <Box
              sx={{
                gridColumn: '1 / -1',
                p: 2.25,
                borderRadius: '12px',
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
                minWidth: 0,
                [ROW_WIDE]: { flexDirection: 'row', alignItems: 'center' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={CAPTION_SX}>Адрес аптеки</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 0.5, wordBreak: 'break-word' }}>
                  {filled(profile.pharmacyAddress || profile.address, 'Местоположение еще не передано')}
                </Typography>
              </Box>
              {profile.latitude && profile.longitude && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1,
                    borderRadius: '10px',
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    flexShrink: 0
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 600, display: 'block' }}>
                      ШИРОТА
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
                      {profile.latitude}°
                    </Typography>
                  </Box>
                  <Box sx={{ width: '1px', height: 26, bgcolor: '#e2e8f0' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 600, display: 'block' }}>
                      ДОЛГОТА
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
                      {profile.longitude}°
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Синхронизатор - во всю ширину: одиночная плитка под адресом оставляла пустой ряд */}
            <Box
              sx={{
                gridColumn: '1 / -1',
                p: 2,
                borderRadius: '12px',
                bgcolor: syncState.alarm ? '#fef2f2' : '#ffffff',
                border: syncState.alarm ? '1.5px solid #fecaca' : '1px solid #e2e8f0',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                columnGap: 2,
                rowGap: 1.5,
                minWidth: 0
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: syncState.dot, flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ ...CAPTION_SX, color: syncState.alarm ? '#991b1b' : '#64748b' }}>
                    Синхронизатор
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 700, color: syncState.alarm ? '#b91c1c' : '#1e293b', mt: 0.5, fontSize: '1.05rem' }}>
                  {syncState.text}
                </Typography>
                <Typography variant="caption" sx={{ color: syncState.alarm ? '#b91c1c' : '#94a3b8' }}>
                  {syncState.hint}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={onRefreshSync}
                disabled={syncChecking}
                startIcon={syncChecking ? <CircularProgress size={12} color="inherit" /> : <Refresh sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#475569', borderColor: '#cbd5e1', borderRadius: '8px', flexShrink: 0 }}
              >
                Проверить
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ProfileInfoSection;
